import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useCallback, useEffect, useMemo, useRef } from "react";

import { useRootDispatch, useRootSelector } from "../../../store/hooks";
import {
  clearSlide,
  updateNav,
  updateSlide,
} from "../../../store/common/common-slice";

import { ItemModal } from "@firstech-cloud-saas/firstech-ui/src/component";

import classes from "./Cockpit.module.scss";
import {
  getAccountRestrictions,
  selectedAccountInfo,
} from "store/account/account-selector";
import { clearSlideoutNotifications } from "store/notification/notification-slice";

export const LeftDrawer = () => {
  const { t } = useTranslation(["common", "account_next"]);
  const refLeftBar = useRef<any>();
  const navigate = useNavigate();
  const { isSlideOpen, isNavOpen } = useRootSelector((state) => state.common);
  const { paymentCenter } = useRootSelector((state) => state.config);
  const accountInfo = useRootSelector(selectedAccountInfo);
  const { restrictions } = useRootSelector(getAccountRestrictions);

  const dispatch = useRootDispatch();

  const items: ItemModal[] = useMemo(() => {
    const tempItem = [
      { id: "Home", icon: "home", label: t("home_label") },
      {
        id: "MakeAPayment",
        icon: "credit_card",
        label: t("make_payment_label"),
        disabled:
          accountInfo?.active !== true ||
          (restrictions.achRestricted && restrictions.cardRestricted),
      },
      {
        id: "ManageWallet",
        icon: "account_balance_wallet",
        label: t("manage_wallet"),
      },
      // { id: "Confirmation", icon: "phone_in_talk", label: "Menu Book" },
      // { id: "3", icon: "textsms", label: "Lunch Dining" },
      // { id: "4", icon: "person", label: "Agriculture" },
      // { id: "5", icon: "request_quote", label: "EV Station" },
    ];
    if (paymentCenter.statementsEnabled) {
      tempItem.push({
        id: "Statements",
        icon: "receipt_long",
        label: t("account_next:eStatements"),
      });
    }
    if (paymentCenter.payByText.enabled) {
      tempItem.push({
        id: "PayByText",
        icon: "sms",
        label: t("paybytext_label"),
        disabled:
          accountInfo?.active !== true ||
          (restrictions.achRestricted && restrictions.cardRestricted),
      });
    }
    return tempItem;
  }, [
    t,
    paymentCenter.statementsEnabled,
    paymentCenter.payByText,
    accountInfo,
    restrictions,
  ]);

  const navItemclickListener = useCallback(
    (e: CustomEvent<any>) => {
      if (isNavOpen) {
        dispatch(updateNav(false));
        if (!!refLeftBar.current) {
          refLeftBar.current.open = false;
        }
      }
      if (e.detail.id === "Home") {
        navigate(`/${paymentCenter.customerKey}/dashboard`);

        if (isSlideOpen) {
          dispatch(clearSlideoutNotifications());
          dispatch(clearSlide());
        }
        return;
      }
      dispatch(updateSlide(e.detail));
    },
    [dispatch, paymentCenter.customerKey, navigate, isNavOpen, isSlideOpen],
  );

  useEffect(() => {
    setTimeout(() => {
      if (!!refLeftBar.current) {
        refLeftBar.current.inert = false;
      }
    }, 1000);
  }, [isSlideOpen]);

  const navOpenListener = useCallback(
    (e: any) => {
      dispatch(updateNav(e.detail));
    },
    [dispatch],
  );

  useEffect(() => {
    if (isSlideOpen && isNavOpen === true) {
      dispatch(updateNav(false));
      if (!!refLeftBar.current) {
        refLeftBar.current.open = false;
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isSlideOpen, dispatch]);

  useEffect(() => {
    const leftBar = refLeftBar.current;
    if (leftBar) {
      leftBar.addEventListener("itemClicked", navItemclickListener);
      leftBar.addEventListener("ft-left-drawer-slide-change", navOpenListener);
    }

    return () => {
      if (leftBar) {
        leftBar.removeEventListener("itemClicked", navItemclickListener);
        leftBar.removeEventListener(
          "ft-left-drawer-slide-change",
          navOpenListener,
        );
      }
    };
  }, [navItemclickListener, navOpenListener]);

  return (
    <ft-left-drawer
      role="navigation"
      class={classes.LeftDrawer}
      ref={refLeftBar}
      items={JSON.stringify(items)}
    ></ft-left-drawer>
  );
};
